/**
 * warningSigns.js — TEST 3.13, client half (patient push 3a) + R25 and R24 (patient P1). What
 * "Watch for these" shows for each warning sign.
 *
 * ★★★ THE DEFECT (Alex's screenshot, 2026-09-30, an ear case with 4 signs). ViewSummary rendered
 *   `redFlags.map((fl, i) => … redFlagDesc[i] || fl.description || fl.relevance …)`: the TITLE
 *   came from the clinical analysis (`redFlags[i].finding`) and the TEXT from the patient-summary
 *   call (`redFlagDescriptions[i]`), two lists that orella-api's unified summary generates
 *   independently and that are paired ONLY by position. The model reordered them, and the
 *   patient read the advice for bloody discharge under "Conductive hearing loss".
 *
 * ★★ THE PRE-D5b SHAPE (orella-api main 9684497, api/ai_service.py, measured 2026-10-01):
 *     redFlags            [{ finding, relevance, urgency }]   — NO `description` field
 *     redFlagDescriptions ['patient sentence', …]             — plain strings
 *   `relevance` is the clinician's text ("for physician record"). IT IS NEVER SHOWN HERE: the
 *   patient reads the title and the patient sentence, or the title alone.
 *
 * ★★ THE SHAPE (orella-api D5b step 5, 82402d0, not live; R24): redFlagDescriptions
 *   [{ sign, patientTitle, advice }], keyed by each flag's own finding; `patientTitle` is the
 *   same sign in plain words, in the patient's language ('' when the model gave none).
 *
 * ★★ THE RULE (R24: "a plain-language patient title beside the clinical term"):
 *   - A flag shows its clinical term (`finding`) and, beside it, the patientTitle of the keyed
 *     entry whose `sign` is that finding. A title missing, equal to the clinical term, or equal
 *     to the clinician's relevance shows nothing extra: never a neighbour's title, never the
 *     term twice.
 *   - The advice is the keyed entry's `advice` for that finding.
 *   - ★ NOTHING IS PAIRED BY POSITION ANY MORE. Push 3a (and R25, the step before this one) kept
 *     the pre-D5b plain-string list at the flag's index until the API sent keys; D5b does (and
 *     stores only them), so a plain string is never shown and a summary stored before D5b shows
 *     its titles without that text (accepted under R24, review r1 S2).
 *   - Nothing for the flag: NO TEXT — the title stands alone. (R25 / PROD-139, patient P1: the
 *     flag's own `description` used to stand in; the patient reads patient wording only, and an
 *     advice or sentence that is the clinician's `relevance` is not patient wording.)
 *   - A missing or unknown urgency reads 'moderate', as orella-api D5b stores it (R25), so a sign
 *     is not flat on the first viewing and expandable on the second.
 *   - The live stream sends each sign as ONE object { finding, patientTitle, relevance, urgency,
 *     description }; keyedFromFlags turns those into keyed entries FROM THE SAME OBJECT, so the
 *     first viewing reads exactly what the stored summary (keyed, D5b) reads the second time.
 *
 * Pure (no imports). scripts/check-warning-signs-keyed.mjs executes it.
 */
export const signKey = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/[.!;:,]+$/, '').trim()

// R25 / PROD-139: a missing or unknown urgency is 'moderate' (orella-api D5b step 6 stores it so).
const URGENCIES = ['high', 'moderate', 'low']
export const urgencyOf = (u) => {
  const v = String(u ?? '').trim().toLowerCase()
  return URGENCIES.includes(v) ? v : 'moderate'
}

const isKeyed = (d) => !!d && typeof d === 'object' && typeof d.sign === 'string' && typeof d.advice === 'string'
const text = (v) => (typeof v === 'string' ? v.trim() : '')

/** The stream's REDFLAGS objects as keyed entries, each built from its own object. */
export function keyedFromFlags(flags) {
  return (Array.isArray(flags) ? flags : [])
    .filter((f) => f && typeof f === 'object' && text(f.finding))
    .map((f) => ({ sign: text(f.finding), patientTitle: text(f.patientTitle), advice: text(f.description) }))
}

// ★★ D469 (Dr. Rami 2026-10-10): "only reasons to go to ER or seek care urgently". A sign whose
//   own text calls it reassuring, or a NEGATED finding ("No swelling behind the ear") at low or
//   missing urgency, is a pertinent negative, not a warning sign, and is not shown. A negation
//   alone is not enough: "No urine in 12 hours" at high urgency IS a warning sign. The same rule
//   as orella-api normalize_red_flags (stored writes); this one also covers the live stream and
//   summaries stored before it. check-warning-signs-keyed WS-D469 executes it.
const NEGATED = /^(no|without|absent|absence of|negative for|denies|normal)\b/i
export function isReassurance(flag, advice = '') {
  const f = flag || {}
  const why = [f.relevance, f.description, f.advice, advice].map((v) => String(v ?? '')).join(' ').toLowerCase()
  if (why.includes('reassur')) return true
  const raw = String(f.urgency ?? '').trim().toLowerCase()
  return NEGATED.test(String(f.finding ?? '').trim()) && (raw === '' || raw === 'low')
}

export function warningSignRows(redFlags, descriptions) {
  const flags0 = Array.isArray(redFlags) ? redFlags : []
  const list = Array.isArray(descriptions) ? descriptions : []
  const byKey = new Map()
  for (const d of list) {
    if (!isKeyed(d)) continue
    const k = signKey(d.sign)
    if (k && !byKey.has(k)) byKey.set(k, { advice: d.advice.trim(), patientTitle: text(d.patientTitle) })
  }
  const flags = flags0.filter((fl) => !isReassurance(fl, (byKey.get(signKey((fl || {}).finding)) || {}).advice))
  return flags.map((fl, i) => {
    const f = fl || {}
    const hit = byKey.get(signKey(f.finding))
    const rel = signKey(f.relevance)
    const title = hit ? hit.patientTitle : ''
    const showTitle = !!title && signKey(title) !== signKey(f.finding) && !(rel && signKey(title) === rel)
    // R25 / PROD-139: patient wording only — the keyed advice; never the flag's own fields, never
    // the relevance. R24: never a plain string by position.
    const keyedAdvice = hit && hit.advice && !(rel && signKey(hit.advice) === rel) ? hit.advice : ''
    const advice = keyedAdvice
    return {
      key: `${signKey(f.finding) || 'sign'}-${i}`,
      finding: f.finding || '',
      patientTitle: showTitle ? title : '',
      advice,
      fromKeyedAdvice: !!keyedAdvice,
      urgency: urgencyOf(f.urgency),
      // not for display: the Learn more fetch sends it to the API as context (ViewSummary)
      relevance: f.relevance || '',
    }
  })
}

/** D469: how many signs "Watch for these" will show (the rows warningSignRows keeps). For the
 *  counts outside the warnings view, which must not render the list itself (check-summary-flow). */
export function warningSignCount(redFlags, descriptions) {
  return warningSignRows(redFlags, descriptions).length
}
